// context/PurchaseContext.js - Обновленный контекст для интеграции с новым backend API
import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import templatesApiService from '../services/templatesApi';
import { useAuth } from './AuthContext';
import { PURCHASE_STATUS, PAYMENT_STATUS } from '../types/templates';

// =============================================================================
// НАЧАЛЬНОЕ СОСТОЯНИЕ
// =============================================================================
const initialState = {
  // Шаблоны
  templates: [],
  templateCategories: [],
  popularTemplates: [],
  
  // Покупки и платежи
  purchases: [],
  userContracts: [],
  currentPayment: null,
  
  // Состояния загрузки
  isLoading: false,
  templatesLoading: false,
  contractsLoading: false,
  paymentsLoading: false,
  
  // Ошибки
  error: null,
  
  // Кэш проверок покупок по templateId
  purchaseChecks: {},
  
  // Флаги готовности данных
  contractsLoaded: false,
  templatesLoaded: false,
  purchasesLoaded: false
};

// =============================================================================
// REDUCER
// =============================================================================
const purchaseReducer = (state, action) => {
  switch (action.type) {
    // Общие состояния
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_TEMPLATES_LOADING':
      return { ...state, templatesLoading: action.payload };
    
    case 'SET_CONTRACTS_LOADING':
      return { ...state, contractsLoading: action.payload };
    
    case 'SET_PAYMENTS_LOADING':
      return { ...state, paymentsLoading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    
    case 'CLEAR_ERROR':
      return { ...state, error: null };

    // Шаблоны
    case 'LOAD_TEMPLATES_SUCCESS':
      return {
        ...state,
        templates: action.payload,
        templatesLoaded: true,
        templatesLoading: false
      };

    case 'LOAD_TEMPLATE_CATEGORIES_SUCCESS':
      return { ...state, templateCategories: action.payload };

    case 'LOAD_POPULAR_TEMPLATES_SUCCESS':
      return { ...state, popularTemplates: action.payload };

    // Покупки
    case 'LOAD_PURCHASES_SUCCESS':
      return {
        ...state,
        purchases: action.payload,
        purchasesLoaded: true,
        paymentsLoading: false
      };

    // Договоры
    case 'LOAD_CONTRACTS_SUCCESS':
      return {
        ...state,
        userContracts: action.payload,
        contractsLoaded: true,
        contractsLoading: false
      };

    case 'CREATE_CONTRACT_SUCCESS':
      return {
        ...state,
        userContracts: [...state.userContracts, action.payload]
      };

    case 'UPDATE_CONTRACT_SUCCESS':
      return {
        ...state,
        userContracts: state.userContracts.map(contract =>
          contract.id === action.payload.id ? action.payload : contract
        )
      };

    case 'DELETE_CONTRACT_SUCCESS':
      return {
        ...state,
        userContracts: state.userContracts.filter(contract =>
          contract.id !== action.contractId
        )
      };

    // Платежи
    case 'CREATE_PAYMENT_SUCCESS':
      return {
        ...state,
        currentPayment: action.payload,
        paymentsLoading: false
      };

    case 'PURCHASE_SUCCESS':
      // Обновляем договоры с тем же templateId
      const updatedContracts = state.userContracts.map(contract => {
        if (contract.templateId === action.payload.templateId) {
          return {
            ...contract,
            isPurchased: true,
            purchaseId: action.payload.id
          };
        }
        return contract;
      });

      return {
        ...state,
        purchases: [...state.purchases, action.payload],
        userContracts: updatedContracts,
        currentPayment: null,
        purchaseChecks: {
          ...state.purchaseChecks,
          [action.payload.templateId]: {
            hasPurchase: true,
            canDownload: true,
            purchase: action.payload
          }
        }
      };

    case 'CHECK_PURCHASE':
      return {
        ...state,
        purchaseChecks: {
          ...state.purchaseChecks,
          [action.templateId]: action.result
        }
      };

    default:
      return state;
  }
};

// =============================================================================
// КОНТЕКСТ
// =============================================================================
const PurchaseContext = createContext(null);

// =============================================================================
// ПРОВАЙДЕР
// =============================================================================
export const PurchaseProvider = ({ children }) => {
  const [state, dispatch] = useReducer(purchaseReducer, initialState);
  const { isAuthenticated, user } = useAuth();
  
  // Ref для отслеживания активных запросов (предотвращает дублирование)
  const loadingRef = useRef({
    templates: false,
    categories: false,
    popular: false
  });

  // =============================================================================
  // ЗАГРУЗКА ДАННЫХ
  // =============================================================================
  
  const loadTemplates = async (type = null) => {
    if (loadingRef.current.templates) return;
    
    try {
      loadingRef.current.templates = true;
      dispatch({ type: 'SET_TEMPLATES_LOADING', payload: true });
      const response = await templatesApiService.getTemplates(type);
      
      if (response.status === 'success') {
        dispatch({ type: 'LOAD_TEMPLATES_SUCCESS', payload: response.data });
      } else {
        throw new Error(response.error?.message || 'Ошибка загрузки шаблонов');
      }
    } catch (error) {
      console.error('Ошибка загрузки шаблонов:', error);
      dispatch({ type: 'SET_ERROR', payload: templatesApiService.handleApiError(error) });
    } finally {
      loadingRef.current.templates = false;
      dispatch({ type: 'SET_TEMPLATES_LOADING', payload: false });
    }
  };

  const loadTemplateCategories = async () => {
    if (loadingRef.current.categories) return;

    try {
      loadingRef.current.categories = true;
      const response = await templatesApiService.getTemplateCategories();
      if (response.status === 'success') {
        dispatch({ type: 'LOAD_TEMPLATE_CATEGORIES_SUCCESS', payload: response.data });
      }
    } catch (error) {
      console.error('Ошибка загрузки категорий:', error);
    } finally {
      loadingRef.current.categories = false;
    }
  };

  const loadPopularTemplates = async (limit = 5) => {
    if (loadingRef.current.popular) return;

    try {
      loadingRef.current.popular = true;
      const response = await templatesApiService.getPopularTemplates(limit);
      if (response.status === 'success') {
        dispatch({ type: 'LOAD_POPULAR_TEMPLATES_SUCCESS', payload: response.data });
      }
    } catch (error) {
      console.error('Ошибка загрузки популярных шаблонов:', error);
    } finally {
      loadingRef.current.popular = false;
    }
  };

  const loadUserPurchases = async () => {
    if (!isAuthenticated) return;

    try {
      dispatch({ type: 'SET_PAYMENTS_LOADING', payload: true });
      const response = await templatesApiService.getUserPurchases();
      
      if (response.status === 'success') {
        dispatch({ type: 'LOAD_PURCHASES_SUCCESS', payload: response.data });
      } else {
        throw new Error(response.error?.message || 'Ошибка загрузки покупок');
      }
    } catch (error) {
      console.error('Ошибка загрузки покупок:', error);
      // Не показываем ошибку пользователю для покупок - это не критично
    } finally {
      dispatch({ type: 'SET_PAYMENTS_LOADING', payload: false });
    }
  };

  const loadUserContracts = async () => {
    if (!isAuthenticated) {
      dispatch({ type: 'LOAD_CONTRACTS_SUCCESS', payload: [] });
      return;
    }

    try {
      dispatch({ type: 'SET_CONTRACTS_LOADING', payload: true });
      const response = await templatesApiService.getUserContracts();
      
      if (response.status === 'success') {
        // Backend возвращает { contracts: [], page: 1, totalCount: 0, ... }
        const contracts = response.data.contracts || response.data;
        dispatch({ type: 'LOAD_CONTRACTS_SUCCESS', payload: contracts });
        console.log('Договоры пользователя загружены:', contracts);
      } else {
        throw new Error(response.error?.message || 'Ошибка загрузки договоров');
      }
    } catch (error) {
      console.error('Ошибка загрузки договоров:', error);
      dispatch({ type: 'SET_ERROR', payload: templatesApiService.handleApiError(error) });
    } finally {
      dispatch({ type: 'SET_CONTRACTS_LOADING', payload: false });
    }
  };

  // =============================================================================
  // ОСНОВНЫЕ ДЕЙСТВИЯ
  // =============================================================================
  
  const checkTemplatePurchase = async (templateId) => {
    if (!isAuthenticated) {
      const result = { hasPurchase: false, canDownload: false, purchase: null };
      dispatch({ type: 'CHECK_PURCHASE', templateId, result });
      return result;
    }

    try {
      const response = await templatesApiService.checkTemplatePurchase(templateId);
      
      if (response.status === 'success') {
        const result = {
          hasPurchase: response.data.hasAccess || false,
          canDownload: response.data.hasAccess || false,
          purchase: response.data.purchase || null
        };
        dispatch({ type: 'CHECK_PURCHASE', templateId, result });
        return result;
      } else {
        throw new Error(response.error?.message || 'Ошибка проверки покупки');
      }
    } catch (error) {
      console.error('Ошибка проверки покупки:', error);
      const fallbackResult = { hasPurchase: false, canDownload: false, purchase: null };
      dispatch({ type: 'CHECK_PURCHASE', templateId, result: fallbackResult });
      return fallbackResult;
    }
  };

  const createPayment = async (templateId, userEmail) => {
    if (!isAuthenticated) {
      throw new Error('Необходима авторизация для покупки');
    }

    dispatch({ type: 'SET_PAYMENTS_LOADING', payload: true });
    
    try {
      const response = await templatesApiService.createPayment(templateId, userEmail);
      
      if (response.status === 'success') {
        dispatch({ type: 'CREATE_PAYMENT_SUCCESS', payload: response.data });
        return response.data;
      } else {
        throw new Error(response.error?.message || 'Ошибка создания платежа');
      }
    } catch (error) {
      console.error('Ошибка создания платежа:', error);
      dispatch({ type: 'SET_ERROR', payload: templatesApiService.handleApiError(error) });
      throw error;
    } finally {
      dispatch({ type: 'SET_PAYMENTS_LOADING', payload: false });
    }
  };

  const getPaymentStatus = async (paymentId) => {
    try {
      const response = await templatesApiService.getPaymentStatus(paymentId);
      
      if (response.status === 'success') {
        return response.data;
      } else {
        throw new Error(response.error?.message || 'Ошибка получения статуса платежа');
      }
    } catch (error) {
      console.error('Ошибка получения статуса платежа:', error);
      throw error;
    }
  };

  // =============================================================================
  // МЕТОДЫ ДЛЯ РАБОТЫ С ДОГОВОРАМИ
  // =============================================================================
  
  const createContract = async (templateId, data) => {
    if (!isAuthenticated) {
      throw new Error('Необходима авторизация для создания договора');
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await templatesApiService.createContract(templateId, data);
      
      if (response.status === 'success') {
        dispatch({ type: 'CREATE_CONTRACT_SUCCESS', payload: response.data });
        console.log('Договор создан:', response.data);
        return response.data;
      } else {
        throw new Error(response.error?.message || 'Ошибка создания договора');
      }
    } catch (error) {
      console.error('Ошибка создания договора:', error);
      dispatch({ type: 'SET_ERROR', payload: templatesApiService.handleApiError(error) });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const updateContract = async (contractId, data) => {
    if (!isAuthenticated) {
      throw new Error('Необходима авторизация для обновления договора');
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await templatesApiService.updateContract(contractId, data);
      
      if (response.status === 'success') {
        dispatch({ type: 'UPDATE_CONTRACT_SUCCESS', payload: response.data });
        console.log('Договор обновлен:', response.data);
        return response.data;
      } else {
        throw new Error(response.error?.message || 'Ошибка обновления договора');
      }
    } catch (error) {
      console.error('Ошибка обновления договора:', error);
      dispatch({ type: 'SET_ERROR', payload: templatesApiService.handleApiError(error) });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const deleteContract = async (contractId) => {
    if (!isAuthenticated) {
      throw new Error('Необходима авторизация для удаления договора');
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await templatesApiService.deleteContract(contractId);
      
      if (response.status === 'success') {
        dispatch({ type: 'DELETE_CONTRACT_SUCCESS', contractId });
        console.log('Договор удален:', contractId);
        return response.data;
      } else {
        throw new Error(response.error?.message || 'Ошибка удаления договора');
      }
    } catch (error) {
      console.error('Ошибка удаления договора:', error);
      dispatch({ type: 'SET_ERROR', payload: templatesApiService.handleApiError(error) });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const downloadContract = async (contractId, format = 'word', suggestedFilename = null) => {
    try {
      const result = await templatesApiService.downloadContractFile(contractId, format, suggestedFilename);
      console.log('Договор успешно скачан:', result.data.fileName);
      return result;
    } catch (error) {
      console.error('Ошибка скачивания договора:', error);
      throw new Error(templatesApiService.handleApiError(error));
    }
  };

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  // =============================================================================
  // ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ
  // =============================================================================
  
  const getTemplateById = (templateId) => {
    return state.templates.find(t => t.id === templateId);
  };

  const getPurchaseByTemplateId = (templateId) => {
    return state.purchases.find(p => p.templateId === templateId);
  };

  const canDownloadTemplate = (templateId) => {
    const check = state.purchaseChecks[templateId];
    return check?.canDownload || false;
  };

  const getContractById = (contractId) => {
    return state.userContracts.find(c => c.id === contractId);
  };

  const getContractsByTemplateType = (templateType) => {
    return state.userContracts.filter(c => c.templateType === templateType);
  };

  const getContractStatus = (contract) => {
    if (!contract) return { status: 'unknown', canDownload: false };

    if (contract.isPurchased) {
      return { status: 'purchased', canDownload: true };
    }

    // Проверяем, есть ли покупка для этого шаблона (возможно истекшая)
    const purchase = state.purchases.find(p => p.templateId === contract.templateId);
    if (purchase && purchase.status === PURCHASE_STATUS.EXPIRED) {
      return { status: 'expired', canDownload: false };
    }

    return { status: 'not_purchased', canDownload: false };
  };

  // =============================================================================
  // ЭФФЕКТЫ
  // =============================================================================
  
  useEffect(() => {
    if (!state.templatesLoaded) {
      loadTemplates();
      loadTemplateCategories();
      loadPopularTemplates();
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && user) {
      loadUserPurchases();
      loadUserContracts();
    } else {
      dispatch({ type: 'LOAD_PURCHASES_SUCCESS', payload: [] });
      dispatch({ type: 'LOAD_CONTRACTS_SUCCESS', payload: [] });
    }
  }, [isAuthenticated, user]);

  // =============================================================================
  // ЗНАЧЕНИЕ КОНТЕКСТА
  // =============================================================================
  
  const contextValue = {
    // Состояние
    ...state,

    // Загрузка данных
    loadTemplates,
    loadTemplateCategories,
    loadPopularTemplates,
    loadUserPurchases,
    loadUserContracts,

    // Покупки и платежи
    checkTemplatePurchase,
    createPayment,
    getPaymentStatus,

    // Договоры
    createContract,
    updateContract,
    deleteContract,
    downloadContract,

    // Утилиты
    clearError,
    getTemplateById,
    getPurchaseByTemplateId,
    canDownloadTemplate,
    getContractById,
    getContractsByTemplateType,
    getContractStatus
  };

  return (
    <PurchaseContext.Provider value={contextValue}>
      {children}
    </PurchaseContext.Provider>
  );
};

// =============================================================================
// ХУКИ
// =============================================================================

export const usePurchase = () => {
  const context = useContext(PurchaseContext);
  if (!context) {
    throw new Error('usePurchase должен использоваться внутри PurchaseProvider');
  }
  return context;
};

// Хук для работы с договорами
export const useUserContracts = () => {
  const context = usePurchase();
  return {
    contracts: context.userContracts,
    isLoading: context.contractsLoading,
    error: context.error,
    contractsLoaded: context.contractsLoaded,
    createContract: context.createContract,
    updateContract: context.updateContract,
    deleteContract: context.deleteContract,
    downloadContract: context.downloadContract,
    loadUserContracts: context.loadUserContracts,
    getContractById: context.getContractById,
    getContractsByTemplateType: context.getContractsByTemplateType,
    getContractStatus: context.getContractStatus
  };
};

export default PurchaseContext;